/**
 * Progressive/recursive graph expansion for the Defense Technology
 * Intelligence graph.
 *
 * Routes an expansion request to the appropriate API(s) by node type:
 * - organization → Publications `affiliations` + PLA Contracts
 * - pla          → BOTH APIs (research relationships AND procurement)
 * - person       → Publications by author
 * - technology   → Publications keywords + PLA Contracts
 * - buyer        → PLA Contracts by buyer name
 * - supplier     → PLA Contracts by supplier name
 * - agency       → PLA Contracts by agency name
 *
 * The builder handles fusion, so expansions naturally connect back into the
 * existing graph whenever entities resolve to already-known node ids.
 */
import { IntelNode } from '../../types/intelligenceGraph';
import { IntelligenceGraphBuilder } from './graphBuilder';
import {
  DEFAULT_PUBLICATIONS_PAGE_SIZE,
  searchPublicationsIntoGraph,
} from './publicationsAdapter';
import {
  DEFAULT_PROCUREMENT_PAGE_SIZE,
  ProcurementRootContext,
  searchProcurementIntoGraph,
} from './procurementAdapter';

export interface ExpansionResult {
  publications?: { processed: number; total: number };
  procurement?: { processed: number; total: number; nextPageToken?: string };
  errors: string[];
}

export interface ExpansionOptions {
  /** Result limit per API; defaults to 100 per the analyst workflow spec. */
  size?: number;
  /** Publications result offset for "load more". */
  publicationsOffset?: number;
  /** Procurement page token for "load more". */
  procurementPageToken?: string | null;
  /**
   * Root/search entity context. When present, procurement records that
   * explicitly mention the root are guaranteed an edge back to it so
   * explicitly-sourced records never become orphan nodes.
   */
  root?: ProcurementRootContext;
}

/** True when this node type can be expanded into further API searches. */
export function isExpandableNode(node: IntelNode): boolean {
  return [
    'organization',
    'pla',
    'person',
    'technology',
    'buyer',
    'supplier',
    'agency',
  ].includes(node.type);
}

async function settle<T>(
  promise: Promise<T> | undefined,
  label: string,
  errors: string[],
): Promise<T | undefined> {
  if (!promise) return undefined;
  try {
    return await promise;
  } catch (error) {
    errors.push(
      `${label} search failed: ${
        error instanceof Error ? error.message : 'unknown error'
      }`,
    );
    return undefined;
  }
}

/**
 * Runs a fused search for an organization/entity name against BOTH APIs
 * simultaneously and merges the results into the builder. This is the entry
 * point for the initial root search (e.g. CETC 14) and for org/PLA node
 * expansion.
 */
export async function expandOrganization(
  builder: IntelligenceGraphBuilder,
  name: string,
  options: ExpansionOptions = {},
): Promise<ExpansionResult> {
  const errors: string[] = [];
  const size = options.size ?? DEFAULT_PUBLICATIONS_PAGE_SIZE;
  const [publications, procurement] = await Promise.all([
    settle(
      searchPublicationsIntoGraph(
        builder,
        name,
        'organization',
        size,
        options.publicationsOffset ?? 0,
      ),
      'Publications',
      errors,
    ),
    settle(
      searchProcurementIntoGraph(
        builder,
        name,
        Math.min(options.size ?? DEFAULT_PROCUREMENT_PAGE_SIZE, 100),
        options.procurementPageToken ?? null,
        true,
        options.root,
      ),
      'PLA Contracts',
      errors,
    ),
  ]);
  return { publications, procurement, errors };
}

/**
 * Expands a node by searching the appropriate API(s) for its canonical
 * label, merging discoveries into the existing builder/graph.
 */
export async function expandNode(
  builder: IntelligenceGraphBuilder,
  node: IntelNode,
  options: ExpansionOptions = {},
): Promise<ExpansionResult> {
  const errors: string[] = [];
  const size = options.size ?? DEFAULT_PUBLICATIONS_PAGE_SIZE;

  switch (node.type) {
    case 'organization':
    case 'pla':
      return expandOrganization(builder, node.label, options);

    case 'person': {
      const publications = await settle(
        searchPublicationsIntoGraph(
          builder,
          node.label,
          'person',
          size,
          options.publicationsOffset ?? 0,
        ),
        'Publications',
        errors,
      );
      return { publications, errors };
    }

    case 'technology': {
      const [publications, procurement] = await Promise.all([
        settle(
          searchPublicationsIntoGraph(
            builder,
            node.label,
            'technology',
            size,
            options.publicationsOffset ?? 0,
          ),
          'Publications',
          errors,
        ),
        settle(
          searchProcurementIntoGraph(
            builder,
            node.label,
            Math.min(size, 100),
            options.procurementPageToken ?? null,
            true,
            options.root,
          ),
          'PLA Contracts',
          errors,
        ),
      ]);
      return { publications, procurement, errors };
    }

    case 'buyer':
    case 'supplier':
    case 'agency': {
      const procurement = await settle(
        searchProcurementIntoGraph(
          builder,
          node.label,
          Math.min(size, 100),
          options.procurementPageToken ?? null,
          true,
          options.root,
        ),
        'PLA Contracts',
        errors,
      );
      return { procurement, errors };
    }

    default:
      return { errors: [`Node type "${node.type}" is not expandable`] };
  }
}
